/**
 * ACL Permissions Registry (Browser-safe version)
 *
 * This is a browser-compatible subset of the server permissions registry.
 * Keep in sync with: src/domain/acl/permissions.ts
 */

// ─────────────────────────────────────────────────────────────────────────────
// Administrative Permissions
// ─────────────────────────────────────────────────────────────────────────────

export const ADMIN_PERMISSIONS = {
  "admin.users.read": "View user list and details",
  "admin.users.create": "Create new users",
  "admin.users.update": "Modify user accounts",
  "admin.users.delete": "Remove user accounts",
  "admin.users.impersonate": "Impersonate other users",
  "admin.roles.read": "View roles",
  "admin.roles.manage": "Create, edit, delete roles",
  "admin.groups.read": "View groups",
  "admin.groups.manage": "Create, edit, delete groups",
  "admin.settings.read": "View system settings",
  "admin.settings.update": "Modify system settings",
  "admin.branding.update": "Modify instance branding (logos, favicon, default avatar/banner)",
  "admin.sessions.read": "View all user sessions",
  "admin.sessions.revoke": "Revoke user sessions",
  "admin.system.dashboard": "Access system dashboard",
  "admin.system.logs": "View system logs",
  "admin.system.console": "Access web console",
  "admin.system.database": "Manage database",
  "admin.system.ssl": "Manage SSL certificates",
  "admin.system.vpn": "Manage VPN settings",
  "admin.system.update": "Perform system updates",
  "admin.system.api": "Manage API settings",
  "admin.system.support": "Enable or disable support team access",
  "admin.links.read": "View external links",
  "admin.links.manage": "Manage external links",
  "admin.software.install": "Install software",
  "admin.software.remove": "Remove software",
  "admin.software.configure": "Configure software",
  "admin.apps.config.read": "View app configuration files",
  "admin.apps.config.edit": "Edit app configuration files",
  "admin.apps.backup.create": "Create app backups (config, nginx, full)",
  "admin.apps.backup.restore": "Restore app backups",
  "admin.apps.backup.delete": "Delete app backups",
  "admin.apps.database.browse": "Browse app SQLite database files",
  "admin.streaming.read": "View streaming dashboard",
  "admin.streaming.manage": "Manage streaming settings",
  "admin.requests.read": "View media requests",
  "admin.requests.manage": "Approve or deny media requests",
} as const;

export type AdminPermission = keyof typeof ADMIN_PERMISSIONS;

// ─────────────────────────────────────────────────────────────────────────────
// Admin-scoped feature permissions (browser mirror of the server registry).
// These are admin-only capabilities the sysop can revoke per-admin — NOT
// group-grantable to non-admins. Keep in sync with the server ADMIN_FEATURE_PERMISSIONS.
// ─────────────────────────────────────────────────────────────────────────────

export const ADMIN_FEATURE_PERMISSIONS = {
  "admin.system.deleteProtection": "Manage large-file-delete protection (view status, enable/disable server-wide)",
} as const;

export type AdminFeaturePermission = keyof typeof ADMIN_FEATURE_PERMISSIONS;

/** Feature-permission key gating the Delete Protection admin surface (admin-scoped). */
export const DELETE_PROTECTION_PERMISSION: AdminFeaturePermission =
  "admin.system.deleteProtection";

// ─────────────────────────────────────────────────────────────────────────────
// Application Categories
// ─────────────────────────────────────────────────────────────────────────────

export const APP_CATEGORIES = {
  "media-management": {
    label: "Media Management",
    description: "Apps for organizing and managing media libraries",
    apps: [
      "bazarr",
      "lazylibrarian",
      "lidarr",
      "medusa",
      "mylar3",
      "radarr",
      "readarr",
      "sickchill",
      "sickgear",
      "sonarr",
    ],
  },
  "media-servers": {
    label: "Media Servers",
    description: "Apps for streaming and serving media content",
    apps: [
      "airsonic",
      "emby",
      "jellyfin",
      "plex",
      "subsonic",
      "tautulli",
      "wsdashboard",
      "xteve",
    ],
  },
  "media-requests": {
    label: "Media Requests",
    description: "Apps for managing media requests",
    apps: ["jellyseerr", "ombi", "overseerr", "requestrr", "seerr"],
  },
  "download-clients": {
    label: "Download Clients",
    description: "Torrent and NZB download clients",
    apps: [
      "deluge",
      "flood",
      "jdownloader",
      "nzbget",
      "pyload",
      "qbittorrent",
      "rtorrent",
      "rutorrent",
      "sabnzbd",
      "transmission",
    ],
  },
  indexers: {
    label: "Indexers & Trackers",
    description: "Apps for finding content sources",
    apps: ["autobrr", "autodl", "jackett", "nzbhydra2", "prowlarr"],
  },
  automation: {
    label: "Automation & Tools",
    description: "Apps for automating media workflows",
    apps: [
      "autoscan",
      "filebot",
      "flaresolverr",
      "flexget",
      "headphones",
      "notifiarr",
      "seedcross",
      "unpackerr",
    ],
  },
  "file-management": {
    label: "File Management",
    description: "Apps for managing and syncing files",
    apps: [
      "btsync",
      "duplicati",
      "filebrowser",
      "nextcloud",
      "rclone",
      "syncthing",
    ],
  },
  readers: {
    label: "E-Books & Comics",
    description: "Apps for reading digital content",
    apps: ["calibre", "kavita", "komga"],
  },
  "remote-access": {
    label: "Remote Access",
    description: "Apps for remote system access",
    apps: ["novnc", "webconsole", "x2go"],
  },
  communication: {
    label: "Communication",
    description: "IRC and chat applications",
    apps: ["quassel", "thelounge", "znc"],
  },
  utilities: {
    label: "Utilities & System",
    description: "System utilities and security tools",
    apps: ["fail2ban", "lecert", "netdata", "phpmyadmin", "vpn", "wireguard"],
  },
} as const;

export type AppCategory = keyof typeof APP_CATEGORIES;
export type AppId = (typeof APP_CATEGORIES)[AppCategory]["apps"][number];

// ─────────────────────────────────────────────────────────────────────────────
// Default Roles
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_ROLES = {
  SUPER_ADMIN: {
    id: "super_admin",
    name: "Super Admin",
    description: "Full system access, cannot be removed",
    isSystem: true,
    priority: 100,
  },
  ADMIN: {
    id: "admin",
    name: "Administrator",
    description: "Full administrative access",
    isSystem: true,
    priority: 90,
  },
  STAFF: {
    id: "staff",
    name: "Staff",
    description: "Limited administrative access",
    isSystem: false,
    priority: 50,
  },
  USER: {
    id: "user",
    name: "User",
    description: "Standard user access",
    isSystem: true,
    priority: 10,
  },
  BANNED: {
    id: "banned",
    name: "Banned",
    description: "Banned user - restricted access",
    isSystem: true,
    priority: 0,
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Default Groups
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_GROUPS = {
  ADMINISTRATOR: {
    id: "administrator",
    name: "Administrator",
    description: "Full access to all applications",
    isSystem: true,
    priority: 100,
  },
  ACCESS: {
    id: "access",
    name: "Access",
    description: "Default group with basic download client access",
    isSystem: true,
    priority: 10,
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get all apps for a category
 */
export const getCategoryApps = (categoryId: AppCategory): string[] => {
  return [...(APP_CATEGORIES[categoryId]?.apps ?? [])];
};

/**
 * Get permission domains for UI grouping
 */
export const getPermissionDomains = (): Array<{
  id: string;
  label: string;
  permissions: string[];
}> => {
  const domains: Record<string, string[]> = {};

  for (const key of Object.keys(ADMIN_PERMISSIONS)) {
    const parts = key.split(".");
    const domain = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : parts[0];
    if (!domains[domain]) domains[domain] = [];
    domains[domain].push(key);
  }

  return Object.entries(domains).map(([id, permissions]) => ({
    id,
    label: formatDomainLabel(id),
    permissions,
  }));
};

const formatDomainLabel = (domain: string): string => {
  const parts = domain.split(".");
  if (parts.length < 2) return domain;
  const subject = parts[1];
  return subject.charAt(0).toUpperCase() + subject.slice(1);
};

/**
 * Get app categories as a list for UI
 */
export const getAppCategoryList = (): Array<{
  id: AppCategory;
  label: string;
  description: string;
  apps: string[];
}> => {
  return Object.entries(APP_CATEGORIES).map(([id, cat]) => ({
    id: id as AppCategory,
    label: cat.label,
    description: cat.description,
    apps: [...cat.apps],
  }));
};
